<script setup>
import { useRoute } from '@/Utils/useRoute';
import { Link } from '@inertiajs/vue3';
import NewsletterSignup from '@/Components/NewsletterSignup.vue';
import CookieConsent from '@/Components/CookieConsent.vue';

const route = useRoute();
</script>

<template>
  <footer class="footer">
    <div class="footer-inner">
      <div class="footer-grid">
        <!-- Logo + Tagline -->
        <div class="footer-col">
          <div class="footer-logo">
            <span class="footer-logo-text">PriceCompare</span>
          </div>
          <p class="footer-tagline">
            Find de bedste tilbud og sammenlign priser fra danske forhandlere af IT og hardware.
          </p>
          <NewsletterSignup />
        </div>

        <!-- Kategorier -->
        <div class="footer-col">
          <div class="footer-heading">Kategorier</div>
          <ul class="footer-links">
            <li><Link :href="route('frontpage')">Forside</Link></li>
            <li><Link :href="route('categories')">Kategorier</Link></li>
            <li><Link :href="route('articles')">Artikler</Link></li>
            <li><Link :href="route('brands.index')">Brands</Link></li>
            <li><Link :href="route('retailers.index')">Forhandlere</Link></li>
            <li><Link :href="route('blackFriday')">Black Friday</Link></li>
            <li><Link :href="route('pcbygger.index')">PC-byggeren</Link></li>
            <!--
              Spilsiderne stod kun at finde gennem de gamle compware-links.
              Uden et internt link ville de vaere foraeldreloese sider, og
              det er praecis den slags Google holder ude af indekset.
            -->
            <li><Link :href="route('spil.index')">PC til dine spil</Link></li>
          </ul>
          <div class="footer-subsection" style="margin-top: 16px;">
            <div class="footer-heading">Populære</div>
            <ul class="footer-links">
              <li><a href="/kategorier/computer/hardware/bundkort">Bundkort</a></li>
              <li><a href="/kategorier/computer/hardware/pc-skaerm">PC Skærme</a></li>
              <li><a href="/kategorier/computer/hardware/processor-cpu">Processorer</a></li>
              <li><a href="/kategorier/computer/hardware/grafikkort">Grafikkort</a></li>
              <li><a href="/kategorier/computer/netvaerk/nas-server">NAS Servere</a></li>
              <li><a href="/kategorier/computer/computer-tilbehoer/3d-printer">3D Printere</a></li>
            </ul>
          </div>
        </div>

        <!-- Om os -->
        <div class="footer-col">
          <div class="footer-heading">Om os</div>
          <ul class="footer-links">
            <li><Link :href="route('about')">Om os</Link></li>
            <li><Link :href="route('contact')">Kontakt</Link></li>
            <li><a href="https://www.fridayblackdanmark.dk/" rel="noopener">Friday Black Danmark</a></li>
          </ul>
        </div>

        <!-- Hjælp -->
        <div class="footer-col">
          <div class="footer-heading">Hjælp</div>
          <ul class="footer-links">
            <li><Link :href="route('faq')">FAQ</Link></li>
            <li><Link :href="route('terms')">Vilkår</Link></li>
            <li><Link :href="route('privacy')">Privatpolitik</Link></li>
            <li><Link :href="route('cookie.preferences')">Cookies</Link></li>
            <li><Link :href="route('beta')">Beta</Link></li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <p class="footer-copyright">
          &copy; 2026 PriceCompare.dk &middot; Dansk prisportal for IT og hardware
        </p>
      </div>
    </div>
  </footer>
  <CookieConsent />
</template>

<style scoped>
.footer {
  background: var(--pc-bg-tertiary);
  border-top: 1px solid var(--pc-border);
  padding: 48px 24px;
}

.footer-inner {
  max-width: var(--pc-max-width);
  margin: 0 auto;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 32px;
}

.footer-col {
  min-width: 0;
}

.footer-logo {
  margin-bottom: 12px;
}

.footer-logo-text {
  font-size: 1.25rem;
  font-weight: 700;
  background: var(--pc-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.footer-tagline {
  color: var(--pc-text-secondary);
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
}

.footer-heading {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--pc-text-muted);
  letter-spacing: 0.05em;
  margin: 0 0 16px 0;
}

.footer-links {
  list-style: none;
  margin: 0;
  padding: 0;
}

.footer-links li {
  margin-bottom: 10px;
}

.footer-links li:last-child {
  margin-bottom: 0;
}

.footer-links a {
  color: var(--pc-text-secondary);
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.15s ease;
}

.footer-links a:hover {
  color: var(--pc-text-primary);
}

.footer-bottom {
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid var(--pc-border);
}

.footer-copyright {
  color: var(--pc-text-muted);
  font-size: 0.8125rem;
  margin: 0;
  text-align: center;
}

/* Tablet: 2 columns */
@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px 24px;
  }
}

/* Mobile: 1 column stacked */
@media (max-width: 480px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}
</style>
